import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';

import { colors } from './theme';

// Redesign type system — Archivo for headings/brand/buttons
const HEADING_FONT = "var(--font-archivo), 'Archivo', system-ui, sans-serif";

declare module '@mui/material/styles' {
  interface TypeBackground {
    subtle: string;
    hover: string;
  }
  interface PaletteColor {
    bg: string;
    border: string;
  }
  interface SimplePaletteColorOptions {
    bg?: string;
    border?: string;
  }
}

// Common theme options shared between light and dark
const commonOptions: ThemeOptions = {
  typography: {
    fontFamily: "var(--font-plex-sans), 'IBM Plex Sans', system-ui, -apple-system, sans-serif",
    // Headings/brand use Archivo per the redesign
    h1: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontFamily: HEADING_FONT, fontWeight: 700 },
    h4: { fontFamily: HEADING_FONT, fontWeight: 700 },
    h5: { fontFamily: HEADING_FONT, fontWeight: 600 },
    h6: { fontFamily: HEADING_FONT, fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontFamily: HEADING_FONT, fontWeight: 600, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 14px',
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: ({ theme }) => ({
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
          '&:hover': {
            background: theme.palette.primary.dark,
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
            transform: 'translateY(-2px)',
          },
        }),
        // Flat solid fills (no gradient sheen) to match the redesign
        containedSuccess: {
          background: colors.accent.green.dark,
          color: '#062015',
          boxShadow: 'none',
          '&:hover': {
            background: colors.accent.green[300],
            boxShadow: 'none',
          },
        },
        containedError: {
          background: colors.accent.red.dark,
          color: '#210b06',
          boxShadow: 'none',
          '&:hover': {
            background: colors.accent.red.light,
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        // @ts-expect-error - Complex theme callback mismatch
        root: {
          borderRadius: 18,
          boxShadow: (theme: any) =>
            theme.palette.mode === 'dark'
              ? `0 4px 20px ${alpha(theme.palette.common.black, 0.4)}`
              : `0 4px 20px ${alpha(theme.palette.text.primary, 0.08)}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...commonOptions,
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary.light, // #2563eb — CTAs/active states
      light: colors.primary[500],
      dark: colors.primary[700],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.accent.purple[700],
      light: colors.accent.purple[400],
      dark: colors.accent.purple[800],
      contrastText: '#ffffff',
    },
    success: {
      main: colors.accent.green[500],
      light: colors.accent.green[400],
      dark: colors.accent.green[600],
      contrastText: '#ffffff',
      bg: colors.success.bg,
      border: colors.success.border,
    },
    error: {
      main: colors.accent.red[500],
      light: colors.accent.red[400],
      dark: colors.accent.red[600],
      contrastText: '#ffffff',
      bg: colors.error.bg,
      border: colors.error.border,
    },
    warning: {
      main: colors.accent.orange[500],
      light: colors.accent.orange[400],
      dark: colors.accent.orange[600],
      contrastText: '#ffffff',
      bg: colors.warning.bg,
      border: colors.warning.border,
    },
    info: {
      main: colors.accent.blue[500],
      light: colors.accent.blue[400],
      dark: colors.accent.blue[600],
      contrastText: '#ffffff',
      bg: colors.info.bg,
      border: colors.info.border,
    },
    background: {
      default: colors.bg.primary.light,
      paper: colors.bg.secondary.light,
      subtle: colors.bg.subtle.light,
      hover: colors.bg.hover.light,
    },
    text: {
      primary: colors.text.primary.light,
      secondary: colors.text.secondary.light,
      disabled: colors.text.muted.light,
    },
    divider: colors.border.default.light,
    action: {
      hover: colors.bg.hover.light,
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...commonOptions,
  palette: {
    mode: 'dark',
    primary: {
      // Same CTA blue, brightened slightly for contrast on the dark surface.
      main: colors.primary.dark, // #3b82f6
      light: colors.primary[400],
      dark: colors.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.accent.purple.dark,
      light: colors.accent.purple[300],
      dark: colors.accent.purple[700],
      contrastText: '#ffffff',
    },
    success: {
      main: colors.accent.green.dark,
      light: colors.accent.green[300],
      dark: colors.accent.green[500],
      contrastText: '#ffffff',
      bg: colors.success.bg,
      border: colors.success.border,
    },
    error: {
      main: colors.accent.red.dark,
      light: colors.accent.red[300],
      dark: colors.accent.red[500],
      contrastText: '#ffffff',
      bg: colors.error.bg,
      border: colors.error.border,
    },
    warning: {
      main: colors.accent.orange.dark,
      light: colors.accent.orange[300],
      dark: colors.accent.orange[500],
      contrastText: '#ffffff',
      bg: colors.warning.bg,
      border: colors.warning.border,
    },
    info: {
      main: colors.accent.blue.dark,
      light: colors.accent.blue[300],
      dark: colors.accent.blue[500],
      contrastText: '#ffffff',
      bg: colors.info.bg,
      border: colors.info.border,
    },
    background: {
      default: colors.bg.primary.dark,
      paper: colors.bg.secondary.dark,
      subtle: colors.bg.subtle.dark,
      hover: colors.bg.hover.dark,
    },
    text: {
      primary: colors.text.primary.dark,
      secondary: colors.text.secondary.dark,
      disabled: colors.text.muted.dark,
    },
    divider: colors.border.default.dark,
    action: {
      hover: colors.bg.hover.dark,
    },
  },
});
